import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RecordPeek, PeekLink, type PeekTarget } from '../components/RecordPeek';
import { api } from '../lib/api';
import { canDelete, can, useAuth, ROLE } from '../lib/auth';

export function formatJc(jobId: number | string): string {
  if (!jobId) return '';
  const s = String(jobId);
  const i = s.lastIndexOf('/');
  return i === -1 ? s : s.slice(i + 1);
}

interface Widgets {
  employees: { id: string; name: string; designation: string | null; phone: string | null }[];
  holidays: { id: string; name: string; date: string | null }[];
  news: { id: string; title: string; description: string | null; createdOn: string | null }[];
  birthdays: { id: string; name: string; dob: string | null }[];
  stats: { activeJobs: number; pendingJobs: number; activePos: number; activeClients: number; activeProducts: number };
}

interface JobRow {
  id: string; poRefNumber: string; jobName: string; jobOrderDate: string;
  totalBoxes: string; ups: string; totalSheetWithWastage: string;
  paperType: string; sheetSize: string; laminationType: string;
  sheetCutSizeLength: string; sheetCutSizeWidth: string;
  dieCutting: string; selectedDie: string; jobStatus: string;
}
interface JobList { rows: JobRow[]; total: number }

interface PendingJobRow {
  poId: string;
  poNumber: string | null;
  client: string | null;
  productName: string | null;
  jobName: string | null;
  orderType: string | null;
  qty: string | null;
  jcQty: number | null;
  promiseDate: string | null;
  boardStatus: string | null;
  boardExpectedDate: string | null;
  clientId: string | null;
  productId: string | null;
  poDate: string | null;
  crmName: string | null;
  rate: string | null;
  ups: string | null;
  jobId: string | null;
  linkedPoCount: number | null;
  designStatus?: string | null;
  designDoneByName?: string | null;
}

/**
 * Every row of a flow queue, page by page.  The queue serves at most 200 rows
 * a page; the dashboard counts and filters on its side, so it must have all
 * of them or the counts quietly stop at the first 200.
 */
/** The whole queue in one request (the API's ?all=1) - not page by page. */
export async function fetchAllQueue<T>(params: Record<string, string>): Promise<{ rows: T[]; total: number }> {
  const r = await api<{ rows: T[]; total: number }>(`/api/flow/queue?${new URLSearchParams({ ...params, all: '1' })}`);
  return { rows: r.rows, total: r.total ?? r.rows.length };
}

export const num = (v: string | null) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
/** Sheets needed = boxes ÷ ups, rounded up. 0 when ups is unknown. */
export const sheetsFor = (r: { qty: string | null; ups: string | null }) => {
  const u = num(r.ups);
  return u > 0 ? Math.ceil(num(r.qty) / u) : 0;
};
export const money = (n: number) =>
  n.toLocaleString('en-IN', { maximumFractionDigits: 0 });

export function DashboardPage() {
  const { user } = useAuth();
  const isCrm = user?.rollId === ROLE.CRM;
  const isAdmin = user?.rollId === ROLE.Admin;
  const isDesigner = user?.rollId === ROLE.Designers;
  const isCutting = user?.rollId === ROLE.Cutting;
  const seesOversight = can(user, 'VIEW_OVERSIGHT') && !isAdmin;
  const widgets = useQuery({ queryKey: ['dashboard-widgets'], queryFn: () => api<Widgets>('/api/dashboard/widgets') });
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [applied, setApplied] = useState<{ q: string; from: string; to: string } | null>(null);
  const [peek, setPeek] = useState<PeekTarget | null>(null);

  const jobs = useQuery({
    enabled: !!applied,
    queryKey: ['dashboard-jobs', applied],
    queryFn: () => {
      const sp = new URLSearchParams({ page: '1', pageSize: '50' });
      if (applied!.q) sp.set('q', applied!.q);
      if (applied!.from) sp.set('from', applied!.from);
      if (applied!.to) sp.set('to', applied!.to);
      return api<JobList>(`/api/job-orders?${sp.toString()}`);
    },
  });

  const qc = useQueryClient();
  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    setApplied({ q: search, from, to });
  }

  const stats = widgets.data?.stats;

  interface DodAlert {
    poId: string; poNumber: string | null; client: string | null;
    jobName: string | null;
    /** The DOD the job card in hand is held to: its batch DOD, else the PO's (first card only). */
    promiseDate: string;
    jcDate: string | null;
    jobId: string | null; jcQty: number | null; qty: string | null; bucket: string;
    linkedPoCount?: number | null;
  }
  /** The job card's quantity; the PO's (marked) while no job card is raised. */
  const jcQtyText = (r: DodAlert) => (r.jobId && r.jcQty != null
    ? Number(r.jcQty).toLocaleString('en-IN')
    : r.qty ? <span className="text-slate-400" title="No job card yet: the PO quantity">{Number(r.qty).toLocaleString('en-IN')} (PO)</span> : '—');
  // Designers don't dispatch, so they don't need the overdue/today dispatch
  // panel taking up their screen.  The query is disabled for them so we
  // don't do the roundtrip either.
  const dodAlerts = useQuery({
    enabled: !isDesigner && !isCutting,
    queryKey: ['dashboard-dod-alerts'],
    queryFn: () => api<{ today: DodAlert[]; overdue: DodAlert[] }>('/api/dashboard/dod-alerts'),
    staleTime: 60_000,
  });
  const todayDod = dodAlerts.data?.today ?? [];
  const overdueDod = dodAlerts.data?.overdue ?? [];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-brand">Welcome, {user?.name}</h1>
        <p className="text-sm text-slate-500">{new Date().toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</p>
      </div>

      {isDesigner && <DesignerDashboardPanel userId={user?.userId ?? 0} setPeek={setPeek} />}

      {!isDesigner && !isCutting && (overdueDod.length > 0 || todayDod.length > 0) && (
        <div className="space-y-4">
          {overdueDod.length > 0 && (
            <div className="card border-l-4 border-red-500">
              <div className="card-header flex justify-between items-center bg-red-50">
                <span className="font-semibold text-red-700">Overdue — Past DOD, Not Dispatched</span>
                <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full font-medium">{overdueDod.length}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-red-50 text-left text-xs uppercase text-red-700">
                    <tr>
                      <th className="px-3 py-2">PO #</th>
                      <th className="px-3 py-2">Client</th>
                      <th className="px-3 py-2">Product</th>
                      <th className="px-3 py-2 text-right">JC Qty</th>
                      <th className="px-3 py-2">JC Date</th>
                      <th className="px-3 py-2">DOD</th>
                      <th className="px-3 py-2">Days Over</th>
                      <th className="px-3 py-2">JC #</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overdueDod.map((r) => {
                      const days = Math.floor((Date.now() - new Date(r.promiseDate).getTime()) / 86400000);
                      return (
                        <tr key={r.poId} className="border-t border-red-100 bg-red-50/50 hover:bg-red-100/50">
                          <td className="px-3 py-2 font-mono text-xs">{r.poNumber || r.poId}</td>
                          <td className="px-3 py-2 text-xs">{r.client}</td>
                          <td className="px-3 py-2 text-xs">{r.jobName}</td>
                          <td className="px-3 py-2 text-right text-xs">{jcQtyText(r)}</td>
                          <td className="px-3 py-2 text-xs">{r.jcDate ? new Date(r.jcDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : <span className="text-slate-400">no JC yet</span>}</td>
                          <td className="px-3 py-2 text-xs font-semibold text-red-700">{new Date(r.promiseDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</td>
                          <td className="px-3 py-2 text-xs font-bold text-red-700">{days}d</td>
                          <td className="px-3 py-2 font-mono text-xs">
                            {r.jobId ? formatJc(r.jobId) : <span className="text-slate-400">—</span>}
                            {(r.linkedPoCount ?? 0) > 1 && <span className="ml-1 inline-block rounded bg-blue-100 px-1 text-[10px] font-semibold text-blue-700">{r.linkedPoCount}PO</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {todayDod.length > 0 && (
            <div className="card border-l-4 border-amber-500">
              <div className="card-header flex justify-between items-center bg-amber-50">
                <span className="font-semibold text-amber-700">Due Today — Dispatch Required</span>
                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium">{todayDod.length}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-amber-50 text-left text-xs uppercase text-amber-700">
                    <tr>
                      <th className="px-3 py-2">PO #</th>
                      <th className="px-3 py-2">Client</th>
                      <th className="px-3 py-2">Product</th>
                      <th className="px-3 py-2 text-right">JC Qty</th>
                      <th className="px-3 py-2">JC Date</th>
                      <th className="px-3 py-2">JC #</th>
                    </tr>
                  </thead>
                  <tbody>
                    {todayDod.map((r) => (
                      <tr key={r.poId} className="border-t border-amber-100 hover:bg-amber-50">
                        <td className="px-3 py-2 font-mono text-xs">{r.poNumber || r.poId}</td>
                        <td className="px-3 py-2 text-xs">{r.client}</td>
                        <td className="px-3 py-2 text-xs">{r.jobName}</td>
                        <td className="px-3 py-2 text-right text-xs">{jcQtyText(r)}</td>
                        <td className="px-3 py-2 text-xs">{r.jcDate ? new Date(r.jcDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : <span className="text-slate-400">no JC yet</span>}</td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {r.jobId ? formatJc(r.jobId) : <span className="text-slate-400">—</span>}
                          {(r.linkedPoCount ?? 0) > 1 && <span className="ml-1 inline-block rounded bg-blue-100 px-1 text-[10px] font-semibold text-blue-700">{r.linkedPoCount}PO</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}


{/* Only abhishek2026 — deciding who owns an account is the office head's
          call, not every admin's. The API enforces the same rule, so this is
          hiding, not securing. */}
      {canDelete(user) && <UnmappedClients />}

      {/* News marquee */}
      {(widgets.data?.news.length ?? 0) > 0 && (
        <div className="card border-l-4 border-brand">
          <div className="card-body p-3 overflow-hidden">
            <div className="flex animate-marquee whitespace-nowrap gap-12 text-sm">
              {widgets.data!.news.map((n) => (
                <span key={n.id}>
                  <span className="font-semibold text-brand">📰 {n.title}</span>
                  {n.description && <span className="text-slate-600"> — {n.description.slice(0, 120)}</span>}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
        {/* Search + jobs grid */}
        {!isCrm && !isCutting ? (
          <div className="space-y-4">
            <form className="card" onSubmit={applySearch}>
              <div className="card-header">Search Job Orders</div>
              <div className="card-body grid gap-3 md:grid-cols-4">
                <div className="md:col-span-2">
                  <label className="label">Search</label>
                  <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Job ID, PO #, Job Name…" />
                </div>
                <div>
                  <label className="label">From</label>
                  <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
                </div>
                <div>
                  <label className="label">To</label>
                  <input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
                </div>
                <div className="md:col-span-4">
                  <button className="btn-primary" type="submit">Search</button>
                  {applied && (
                    <button type="button" className="btn-ghost ml-2"
                      onClick={() => { setSearch(''); setFrom(''); setTo(''); setApplied(null); }}>
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </form>

            {applied && (
              <div className="card overflow-hidden">
                <div className="card-header flex justify-between">
                  <span>Results</span>
                  {jobs.data && <span className="text-xs font-normal">{jobs.data.total.toLocaleString()} matched</span>}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
                      <tr>
                        <th className="px-3 py-2">Job ID</th>
                        <th className="px-3 py-2">PO #</th>
                        <th className="px-3 py-2">Job Name</th>
                        <th className="px-3 py-2">JO Date</th>
                        <th className="px-3 py-2 text-right">Boxes</th>
                        <th className="px-3 py-2 text-right">Ups</th>
                        <th className="px-3 py-2 text-right">Sheets+W</th>
                        <th className="px-3 py-2">Die / Design</th>
                        <th className="px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.isLoading && <tr><td colSpan={9} className="p-6 text-center text-slate-500">Loading…</td></tr>}
                      {jobs.isError && <tr><td colSpan={9} className="p-6 text-center text-red-600">Failed: {(jobs.error as Error).message}</td></tr>}
                      {!jobs.isLoading && jobs.data?.rows.length === 0 && (
                        <tr><td colSpan={9} className="p-6 text-center text-slate-500">No records found.</td></tr>
                      )}
                      {jobs.data?.rows.map((r) => (
                        <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-2 font-mono text-xs">{r.id}</td>
                          <td className="px-3 py-2">{r.poRefNumber}</td>
                          <td className="px-3 py-2">{r.jobName}</td>
                          <td className="px-3 py-2 text-xs">{r.jobOrderDate}</td>
                          <td className="px-3 py-2 text-right">{r.totalBoxes}</td>
                          <td className="px-3 py-2 text-right">{r.ups}</td>
                          <td className="px-3 py-2 text-right">{r.totalSheetWithWastage}</td>
                          <td className="px-3 py-2 text-xs">{[r.dieCutting, r.selectedDie].filter(Boolean).join(' / ')}</td>
                          <td className="px-3 py-2 text-xs">{r.jobStatus}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!applied && (
              <div className="card">
                <div className="card-body text-sm text-slate-500">
                  Enter search terms or a date range and click Search. We don't auto-load all 80k jobs.
                </div>
              </div>
            )}
          </div>
        ) : isCrm ? (
          <div className="card h-fit">
            <div className="card-body py-8 text-center text-slate-500 space-y-2">
              <div className="text-xl font-bold text-brand">CRM Portal</div>
              <div className="text-sm max-w-md mx-auto">
                Welcome to your dashboard. You can access your assigned clients' orders and set or revise promised delivery dates (DOD) via the <strong>Set Delivery Date</strong> sidebar menu.
              </div>
            </div>
          </div>
        ) : (
          <div className="card h-fit">
            <div className="card-body py-8 text-center text-slate-500 space-y-2">
              <div className="text-xl font-bold text-brand">Cutting Operator Dashboard</div>
              <div className="text-sm max-w-md mx-auto">
                Welcome. View the <Link to="/queue/cutting" className="text-brand hover:underline">Cutting Queue</Link> in the sidebar to start your work.
              </div>
            </div>
          </div>
        )}

        {/* Sidebar widgets */}
        <div className="space-y-4">
          {(widgets.data?.birthdays.length ?? 0) > 0 && (
            <Widget title="🎂 Birthdays this month">
              <ul className="text-sm divide-y divide-slate-100">
                {widgets.data!.birthdays.map((b) => (
                  <li key={b.id} className="py-1 flex justify-between">
                    <span>{b.name}</span>
                    <span className="text-slate-500 text-xs">{b.dob}</span>
                  </li>
                ))}
              </ul>
            </Widget>
          )}

          <Widget title="Upcoming Holidays">
            <ul className="text-sm divide-y divide-slate-100">
              {(widgets.data?.holidays ?? []).slice(0, 8).map((h) => (
                <li key={h.id} className="py-1 flex justify-between">
                  <span>{h.name}</span>
                  <span className="text-slate-500 text-xs">{h.date ? new Date(h.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : ''}</span>
                </li>
              ))}
            </ul>
            <Link to="/holidays" className="block text-right text-xs text-brand hover:underline mt-2">All holidays →</Link>
          </Widget>

          {user?.rollId === ROLE.SuperAdmin && (
            <Widget title="Team">
              <ul className="text-sm divide-y divide-slate-100 max-h-72 overflow-auto">
                {(widgets.data?.employees ?? []).map((e) => (
                  <li key={e.id} className="py-1">
                    <div className="font-medium text-slate-800">{e.name.trim() || e.id}</div>
                    <div className="text-xs text-slate-500">{e.designation ?? '—'} {e.phone && `· ${e.phone}`}</div>
                  </li>
                ))}
              </ul>
              <Link to="/employees" className="block text-right text-xs text-brand hover:underline mt-2">All employees →</Link>
            </Widget>
          )}
        </div>
      </div>
      {peek && <RecordPeek target={peek} onClose={() => setPeek(null)} />}
    </div>
  );
}

interface UnmappedClient {
  clientId: string;
  clientName: string | null;
  poCount: number;
  totalQty: number | null;
  latestPoNumber: string | null;
  latestPoDate: string | null;
  latestJobName: string | null;
}
interface CrmOption { id: number; name: string }

/**
 * PO_Date is varchar on this schema and holds both "2026-08-07" and "6/22/2026".
 * Date.parse copes with both, but anything it cannot read must show as "—"
 * rather than the literal string "Invalid Date".
 */
function shortDate(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? v
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

/**
 * New clients nobody owns yet, with the CRM to hand them to.
 *
 * One row per CLIENT rather than per PO: assigning covers every order that
 * client has and every one it raises next, so a per-PO list would just repeat
 * the same decision. The row disappears once assigned.
 */
function UnmappedClients() {
  const qc = useQueryClient();
  const [picks, setPicks] = useState<Record<string, number>>({});
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['unmapped-clients'],
    queryFn: () => api<{ rows: UnmappedClient[] }>('/api/flow/unmapped-clients'),
  });
  const crms = useQuery({
    queryKey: ['crms'],
    queryFn: () => api<CrmOption[]>('/api/flow/crms'),
  });

  const assign = useMutation({
    mutationFn: (v: { clientId: string; crmUserId: number }) =>
      api<{ crmName: string; posUpdated: number }>('/api/flow/assign-crm', {
        method: 'POST',
        body: JSON.stringify(v),
      }),
    onSuccess: (res, v) => {
      const name = rows.find((r) => r.clientId === v.clientId)?.clientName ?? v.clientId;
      setError(null);
      setNote(
        `${name} → ${res.crmName}. ${res.posUpdated} open order${res.posUpdated === 1 ? '' : 's'} moved into their queue.`,
      );
      // The mapping changes what CRMs can see, so anything scoped by client is
      // now stale, not just this list.
      qc.invalidateQueries({ queryKey: ['unmapped-clients'] });
      qc.invalidateQueries({ queryKey: ['orders-in-flow'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['flow-queue'] });
    },
    onError: (e) => {
      const msg = (e as Error).message;
      setNote(null);
      setError(
        msg === 'crm_not_found'
          ? 'That CRM account is no longer active — refresh and pick another.'
        : msg === 'client_not_found' ? 'That client no longer exists — refresh the list.'
        : msg === 'delete_not_allowed' ? 'Only abhishek2026 can assign clients to a CRM.'
        : msg,
      );
    },
  });

  const rows = list.data?.rows ?? [];
  const crmOptions = crms.data ?? [];

  // Nothing to decide: say so quietly rather than showing an empty table.
  if (!list.isLoading && rows.length === 0) return null;

  return (
    <div className="card">
      <div className="card-header flex justify-between items-center">
        <span className="font-semibold text-brand">
          Unmapped — new client, needs CRM assignment
          <span className="ml-2 text-xs font-normal text-slate-500">
            one decision covers all of that client's orders
          </span>
        </span>
        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium">
          {rows.length} action required
        </span>
      </div>

      {note && (
        <div className="border-b border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{note}</div>
      )}
      {error && (
        <div className="border-b border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}
      {!crms.isLoading && crmOptions.length === 0 && (
        <div className="border-b border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          No active CRM accounts exist yet — create them on the{' '}
          <Link to="/users" className="underline font-medium">Users</Link> screen first.
        </div>
      )}

      <div className="overflow-x-auto font-sans">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
            <tr>
              <th className="px-3 py-2">Client</th>
              <th className="px-3 py-2">Latest order</th>
              <th className="px-3 py-2">PO date</th>
              <th className="px-3 py-2 text-right">Open POs</th>
              <th className="px-3 py-2 text-right">Boxes</th>
              <th className="px-3 py-2 text-right">Assign CRM</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-500">Loading…</td></tr>
            )}
            {rows.map((r) => {
              const busy = assign.isPending && assign.variables?.clientId === r.clientId;
              const picked = picks[r.clientId];
              return (
                <tr key={r.clientId} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 text-xs font-semibold text-slate-800">
                    {r.clientName}
                    <span className="ml-2 font-mono font-normal text-[10px] text-slate-400">{r.clientId}</span>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.latestJobName}
                    {r.latestPoNumber && (
                      <span className="ml-2 font-mono text-[10px] text-slate-400">{r.latestPoNumber}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{shortDate(r.latestPoDate)}</td>
                  <td className="px-3 py-2 text-right text-xs">{r.poCount}</td>
                  <td className="px-3 py-2 text-right text-xs">{money(Number(r.totalQty ?? 0))}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end items-center gap-2">
                      <select
                        className="input py-1 text-xs max-w-[10rem]"
                        value={picked ?? ''}
                        disabled={busy || crmOptions.length === 0}
                        onChange={(e) =>
                          setPicks((p) => ({ ...p, [r.clientId]: Number(e.target.value) }))
                        }
                      >
                        <option value="">— pick CRM —</option>
                        {crmOptions.map((o) => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </select>
                      <button
                        className="btn-primary text-xs py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                        disabled={!picked || busy}
                        title={picked ? 'Hand this client to the selected CRM' : 'Pick a CRM first'}
                        onClick={() => {
                          if (!picked) return;
                          setNote(null); setError(null);
                          assign.mutate({ clientId: r.clientId, crmUserId: picked });
                        }}
                      >
                        {busy ? 'Assigning…' : 'Assign'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Widget({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="card-header text-xs">{title}</div>
      <div className="card-body">{children}</div>
    </div>
  );
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** YYYY-MM-DD of a date as the user's calendar sees it. */
function localDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Day and month of a birthday. DOB is free text in the legacy table, so this
 * reads it the way /api/dashboard/widgets does when it picks this month's.
 */
function birthdayOf(dob: string | null): { day: number; month: number } | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (!Number.isNaN(d.getTime())) return { day: d.getDate(), month: d.getMonth() };
  const m = dob.match(/^\s*(\d{1,2})[\/\-](\d{1,2})/);
  return m ? { day: Number(m[1]), month: Number(m[2]) - 1 } : null;
}

/** The day a holiday falls on, as YYYY-MM-DD. A date column arrives as midnight UTC. */
function holidayDay(v: string | null): string | null {
  if (!v) return null;
  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(v);
  if (iso?.[1]) return iso[1];
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : localDay(d);
}

/**
 * The dashboard for a role whose work has a page of its own (Quality): the
 * greeting, this month's birthdays and the upcoming holidays — nothing else.
 */
export function GreetingDashboardPage() {
  const { user } = useAuth();
  const widgets = useQuery({ queryKey: ['dashboard-widgets'], queryFn: () => api<Widgets>('/api/dashboard/widgets') });

  const now = new Date();
  const today = localDay(now);
  const tomorrow = localDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1));

  const birthdays = (widgets.data?.birthdays ?? [])
    .map((b) => ({ ...b, on: birthdayOf(b.dob) }))
    .sort((a, b) => (a.on?.day ?? 99) - (b.on?.day ?? 99));

  const holidays = (widgets.data?.holidays ?? [])
    .map((h) => ({ ...h, day: holidayDay(h.date) }))
    .filter((h): h is typeof h & { day: string } => !!h.day && h.day >= today)
    .sort((a, b) => a.day.localeCompare(b.day))
    .slice(0, 8);

  const status = widgets.isLoading
    ? <p className="text-sm text-slate-400">Loading…</p>
    : widgets.isError
      ? <p className="text-sm text-red-600">Could not load.</p>
      : null;

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-5xl">
      <div>
        <h1 className="text-2xl font-semibold text-brand">Welcome, {user?.name}</h1>
        <p className="text-sm text-slate-500">
          {now.toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 items-start">
        <Widget title="🎂 Birthdays this month">
          {status ?? (birthdays.length === 0 ? (
            <p className="text-sm text-slate-500">No birthdays this month.</p>
          ) : (
            <ul className="text-sm divide-y divide-slate-100">
              {birthdays.map((b) => {
                const isToday = !!b.on && b.on.day === now.getDate() && b.on.month === now.getMonth();
                return (
                  <li key={b.id} className={`py-1.5 flex justify-between gap-3 ${isToday ? 'font-semibold text-brand' : ''}`}>
                    <span>{b.name.trim() || b.id}{isToday && ' 🎉'}</span>
                    <span className="text-xs text-slate-500 shrink-0">
                      {isToday ? 'Today' : b.on ? `${String(b.on.day).padStart(2, '0')} ${MONTHS_SHORT[b.on.month] ?? ''}` : b.dob}
                    </span>
                  </li>
                );
              })}
            </ul>
          ))}
        </Widget>

        <Widget title="Upcoming Holidays">
          {status ?? (holidays.length === 0 ? (
            <p className="text-sm text-slate-500">No upcoming holidays.</p>
          ) : (
            <ul className="text-sm divide-y divide-slate-100">
              {holidays.map((h) => (
                <li key={h.id} className="py-1.5 flex justify-between gap-3">
                  <span>{h.name}</span>
                  <span className="text-xs text-slate-500 shrink-0">
                    {h.day === today ? 'Today'
                      : h.day === tomorrow ? 'Tomorrow'
                      : new Date(`${h.day}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}
                  </span>
                </li>
              ))}
            </ul>
          ))}
        </Widget>
      </div>
    </div>
  );
}

interface DesignRow {
  poId: string; poNumber: string | null;
  client: string | null; clientId?: string | null;
  productName: string | null; productId?: string | null;
  jobName?: string | null;
  qty: string | null; orderType: string | null;
  designStatus?: string | null;
  designDoneBy?: number | null;
  designDoneByName?: string | null;
  clientPendingReason?: string | null;
  poDate?: string | null;
  promiseDate?: string | null;
}

function fmtPoDate(d: string | null | undefined): string {
  if (!d) return '—';
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return d;
  return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDod(d: string | null | undefined) {
  if (!d) return <span className="text-slate-400">—</span>;
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return <span>{d}</span>;
  const isPast = parsed.getTime() < new Date().setHours(0, 0, 0, 0);
  return (
    <span className={isPast ? 'font-semibold text-red-600' : 'font-medium text-slate-700'}>
      {parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
    </span>
  );
}

/**
 * The order's product, or — when the PO points at no product — its job name
 * with a warning, so the row is still recognisable and the missing link is
 * visible instead of a blank cell.
 */
function DesignProductCell({ row }: { row: DesignRow }) {
  if (row.productName) return <>{row.productName}</>;
  return (
    <>
      {row.jobName || <span className="text-slate-400">—</span>}
      <div className="text-[10px] font-medium text-amber-700">no product linked</div>
    </>
  );
}

/**
 * Compact panel on the Designer's dashboard.
 *
 *   1. Unclaimed queue — New/Change POs with no job card and nobody
 *      working on them.  Each row has a Claim button.
 *   2. In-progress list — POs any designer has claimed but not yet
 *      marked Done, with the designer's name: the whole team sees who
 *      is on what, the designer's own rows highlighted.  Actions still
 *      live on the /queue/design page so the dashboard stays a
 *      "who's working on what" view.
 */
function DesignerDashboardPanel({ userId, setPeek }: { userId: number; setPeek: (t: PeekTarget) => void }) {
  const qc = useQueryClient();
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['designer-dashboard-unclaimed'] });
    await qc.invalidateQueries({ queryKey: ['designer-dashboard-load'] });
  };
  const act = useMutation({
    mutationFn: (v: { poId: string; action: string }) =>
      api('/api/flow/design-action', { method: 'POST', body: JSON.stringify(v) }),
    onSuccess: refresh,
    // A colleague got there first: show the lists as they are now.
    onError: refresh,
  });
  const actError = act.error
    ? ((act.error as Error).message === 'already_claimed'
        ? 'Another designer claimed that order first — the lists are refreshed.'
        : (act.error as Error).message)
    : null;

  const unclaimed = useQuery({
    queryKey: ['designer-dashboard-unclaimed'],
    // Every unclaimed order, however many pages.
    queryFn: () => fetchAllQueue<DesignRow>({ kind: 'design', sub: 'unclaimed' }),
    // A minute: every designer's screen asking every 15 s kept the server busy.
    refetchInterval: 60_000,
  });

  // Every designer's open work, not only this designer's — the same list
  // Super Admin sees on the Design Queue.
  const load = useQuery({
    queryKey: ['designer-dashboard-load'],
    queryFn: () => api<{ rows: DesignRow[]; total: number }>(
      `/api/flow/queue?kind=design&sub=all&pageSize=100`,
    ),
    refetchInterval: 60_000,
  });
  // null = everyone; otherwise one designer's user id.
  const [who, setWho] = useState<number | null>(null);

  const [find, setFind] = useState('');
  const words = find.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const uAll = unclaimed.data?.rows ?? [];
  const uRows = uAll.filter((r) => {
    const hay = [r.poNumber, r.poId, r.client, r.productName, r.jobName, r.orderType].join(' ').toLowerCase();
    return words.every((w) => hay.includes(w));
  });
  const allRows = load.data?.rows ?? [];
  // One chip per designer with their count, this designer first.
  const designers = [...allRows.reduce((m, r) => {
    if (r.designDoneBy == null) return m;
    const d = m.get(r.designDoneBy) ?? { id: r.designDoneBy, name: r.designDoneByName || `User ${r.designDoneBy}`, n: 0 };
    d.n += 1;
    return m.set(r.designDoneBy, d);
  }, new Map<number, { id: number; name: string; n: number }>()).values()]
    .sort((a, b) => (a.id === userId ? -1 : b.id === userId ? 1 : b.n - a.n || a.name.localeCompare(b.name)));
  const rows = who == null ? allRows : allRows.filter((r) => r.designDoneBy === who);

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <span className="font-semibold">Unclaimed Queue</span>
          <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
            {unclaimed.data?.total ?? 0}
          </span>
        </div>
        {actError && (
          <div className="mx-3 mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {actError}
          </div>
        )}
        {uAll.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 px-3 pt-3">
            <input className="input max-w-xs text-sm" value={find} onChange={(e) => setFind(e.target.value)}
                   placeholder="Search PO, client or product" />
            {(unclaimed.data?.total ?? 0) > uAll.length && (
              <span className="text-xs text-amber-700">Showing the newest {uAll.length} of {unclaimed.data?.total}.</span>
            )}
          </div>
        )}
        {uRows.length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm">
            {uAll.length ? 'Nothing matches the search.' : 'No unclaimed POs.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2">PO</th>
                  <th className="px-3 py-2">Client</th>
                  <th className="px-3 py-2">Product</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">PO Date</th>
                  <th className="px-3 py-2">DOD</th>
                  <th className="px-3 py-2">Owner</th>
                  <th className="px-3 py-2">Stage</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {uRows.map((r) => (
                  <tr key={r.poId} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-xs">
                      <PeekLink title="View this purchase order" onOpen={() => setPeek({ kind: 'po', id: r.poId })}>
                        {r.poNumber || r.poId}
                      </PeekLink>
                    </td>
                    <td className="px-3 py-2 text-xs">{r.client}</td>
                    <td className="px-3 py-2 text-xs"><DesignProductCell row={r} /></td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                        r.orderType === 'New' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'
                      }`}>
                        {r.orderType}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtPoDate(r.poDate)}</td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDod(r.promiseDate)}</td>
                    <td className="px-3 py-2 text-xs">
                      {r.designDoneByName ?? <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                        r.designStatus === 'Claimed' ? 'bg-emerald-50 text-emerald-700'
                        : r.designStatus === 'ApprovalSent' ? 'bg-blue-50 text-blue-700'
                        : r.designStatus === 'ApprovalReceived' ? 'bg-purple-50 text-purple-700'
                        : r.designStatus === 'Done' ? 'bg-teal-50 text-teal-700'
                        : 'bg-slate-50 text-slate-600'
                      }`}>
                        {r.designStatus === 'Claimed' ? 'In Design'
                          : r.designStatus === 'ApprovalSent' ? 'Artwork Sent'
                          : r.designStatus === 'ApprovalReceived' ? 'Awaiting Approval'
                          : r.designStatus === 'Done' ? 'Job Processed'
                          : r.designStatus ?? 'Unclaimed'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {(!r.designStatus || r.designStatus === 'Unclaimed') ? (
                        <button className="btn-primary text-xs py-1"
                                disabled={act.isPending}
                                onClick={() => act.mutate({ poId: r.poId, action: 'claim' })}>
                          {act.isPending && act.variables?.poId === r.poId ? 'Claiming…' : 'Claim'}
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header flex justify-between items-center">
          <span className="font-semibold">Design Work in Progress — all designers</span>
          <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-medium">
            {load.data?.total ?? 0}
          </span>
        </div>
        {designers.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-3 py-2 border-b border-slate-100">
            {[{ id: null as number | null, label: 'Everyone', n: allRows.length },
              ...designers.map((d) => ({ id: d.id as number | null, label: d.id === userId ? `${d.name} (you)` : d.name, n: d.n }))]
              .map((chip) => (
                <button key={chip.id ?? 'all'} type="button"
                        onClick={() => setWho(chip.id)}
                        className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                          who === chip.id
                            ? 'bg-brand text-white border-brand'
                            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                        }`}>
                  {chip.label} <span className="opacity-70">{chip.n}</span>
                </button>
              ))}
          </div>
        )}
        {rows.length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm">
            {who == null ? 'No design work in progress.' : 'Nothing in progress for this designer.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2">PO</th>
                  <th className="px-3 py-2">Client</th>
                  <th className="px-3 py-2">Product</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">PO Date</th>
                  <th className="px-3 py-2">DOD</th>
                  <th className="px-3 py-2">Designer</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const mine = r.designDoneBy === userId;
                  return (
                  <tr key={r.poId} className={`border-t border-slate-100 ${mine ? 'bg-amber-50/60 hover:bg-amber-50' : 'hover:bg-slate-50'}`}>
                    <td className="px-3 py-2 font-mono text-xs">
                      <PeekLink title="View this purchase order"
                                onOpen={() => setPeek({ kind: 'po', id: r.poId })}>
                        {r.poNumber || r.poId}
                      </PeekLink>
                    </td>
                    <td className="px-3 py-2 text-xs">{r.client}</td>
                    <td className="px-3 py-2 text-xs"><DesignProductCell row={r} /></td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                        r.orderType === 'New' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'
                      }`}>
                        {r.orderType}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtPoDate(r.poDate)}</td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDod(r.promiseDate)}</td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {r.designDoneByName
                        ? <span className={mine ? 'font-semibold text-slate-800' : 'font-medium text-slate-700'}>{r.designDoneByName}{mine ? ' (you)' : ''}</span>
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase w-fit ${
                        r.designStatus === 'Claimed' ? 'bg-amber-50 text-amber-700'
                        : r.designStatus === 'ApprovalSent' ? 'bg-blue-50 text-blue-700'
                        : r.designStatus === 'ApprovalReceived' ? 'bg-purple-50 text-purple-700'
                        : r.designStatus === 'Done' ? 'bg-teal-50 text-teal-700'
                        : 'bg-slate-50 text-slate-600'
                      }`}>
                        {r.designStatus === 'Claimed' ? 'In Design'
                          : r.designStatus === 'ApprovalSent' ? 'Artwork Sent'
                          : r.designStatus === 'ApprovalReceived' ? 'Awaiting Approval'
                          : r.designStatus === 'Done' ? 'Job Processed'
                          : (r.designStatus ?? '')}
                      </span>
                      {r.clientPendingReason && (
                        <div className="mt-0.5 text-[10px] font-semibold text-amber-700" title={r.clientPendingReason}>{r.clientPendingReason}</div>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {load.data && load.data.total > allRows.length && (
          <div className="p-2 text-center text-xs text-slate-500 border-t border-slate-100">
            Showing the latest {allRows.length} of {load.data.total} — <Link className="underline text-brand" to="/queue/design">open full queue</Link>
          </div>
        )}
      </div>
    </div>
  );
}

